import ContentSuggestedPromptListItem from "@/content/suggested-prompts/ContentSuggestedPromptListItem"
import { ContentSuggestedPromptsListContentFragment$key } from "@/content/suggested-prompts/__generated__/ContentSuggestedPromptsListContentFragment.graphql"
import { ContentSuggestedPromptsListMutation } from "@/content/suggested-prompts/__generated__/ContentSuggestedPromptsListMutation.graphql"
import { ContentSuggestedPromptsListRefetchQuery } from "@/content/suggested-prompts/__generated__/ContentSuggestedPromptsListRefetchQuery.graphql"
import Relay from "@/relay/relayUtils"
import makeUseStyles from "@assets/style/util/makeUseStyles"
import EditorUtils from "@components/editor/EditorUtils"
import { displayErrorToast, displaySuccessToast } from "@components/toast/ToastProvider"
import {
  DiscoChip,
  DiscoDivider,
  DiscoIcon,
  DiscoIconButton,
  DiscoText,
  DiscoTooltip,
} from "@disco-ui"
import DiscoLoaderDots from "@disco-ui/loader/DiscoLoaderDots"
import { Collapse } from "@material-ui/core"
import Skeleton from "@material-ui/lab/Skeleton"
import useActiveProductOrOrganizationPermissions from "@utils/hook/useActiveProductOrOrganizationPermissions"
import { memo, useCallback, useEffect, useState } from "react"
import { graphql, useMutation, useRefetchableFragment } from "react-relay"

interface Props {
  contentKey: ContentSuggestedPromptsListContentFragment$key
  contentUsageId: string
}

function ContentSuggestedPromptsList({ contentKey, contentUsageId }: Props) {
  const [isExpanded, setIsExpanded] = useState(true)

  const [content, refetch] = useRefetchableFragment<
    ContentSuggestedPromptsListRefetchQuery,
    ContentSuggestedPromptsListContentFragment$key
  >(
    graphql`
      fragment ContentSuggestedPromptsListContentFragment on Content
      @refetchable(queryName: "ContentSuggestedPromptsListRefetchQuery") {
        id
        richEditorDescriptionContent
        hasUnprocessedMediaAssets
        hasProcessedMediaAssets
        brainSearchRelatedQuestions(last: 3) {
          edges {
            node {
              id
              ...ContentSuggestedPromptListItemQuestion
            }
          }
        }
      }
    `,
    contentKey
  )

  const [commit, isMutating] = useMutation<ContentSuggestedPromptsListMutation>(graphql`
    mutation ContentSuggestedPromptsListMutation(
      $input: GenerateContentRelatedQuestionsInput!
    ) {
      generateContentRelatedQuestions(input: $input) {
        relatedQuestions {
          id
        }
        errors {
          field
          message
        }
      }
    }
  `)

  const permission = useActiveProductOrOrganizationPermissions()

  const canManage = permission.has("content.manage")

  const relatedQuestions = Relay.connectionToArray(content.brainSearchRelatedQuestions)

  const classes = useStyles()

  const handleRegenerate = useCallback(
    (e?: React.MouseEvent) => {
      e?.stopPropagation()
      commit({
        variables: {
          input: {
            contentUsageId,
          },
        },
        onCompleted(response) {
          if (response.generateContentRelatedQuestions.errors?.length) {
            displayErrorToast(response.generateContentRelatedQuestions.errors[0].message)
            return
          }
          setIsExpanded(true)
          displaySuccessToast({
            message: "Successfully generated suggested prompts",
          })
          refetch({}, { fetchPolicy: "network-only" })
        },
        onError(error) {
          displayErrorToast(error)
        },
      })
    },
    [commit, contentUsageId, refetch]
  )

  const { richEditorDescriptionContent } = content
  const plainTextDescription = richEditorDescriptionContent
    ? EditorUtils.convertToPlainText(JSON.parse(richEditorDescriptionContent))
    : ""

  const descriptionIsLongEnough = plainTextDescription.length >= 500

  // Determine if we can generate prompts based on description or processed assets
  const canGeneratePrompts = descriptionIsLongEnough || content.hasProcessedMediaAssets

  const getGenerationStatus = () => {
    if (descriptionIsLongEnough) {
      return {
        canGenerate: true,
        message: "Suggested Prompts",
        tooltip: content.hasUnprocessedMediaAssets
          ? "Prompts are generated from your description. Additional media assets are still processing and will enhance future generations."
          : undefined,
      }
    }

    if (content.hasUnprocessedMediaAssets && !content.hasProcessedMediaAssets) {
      return {
        canGenerate: false,
        message:
          "Waiting for media assets to process before generating suggested prompts",
        tooltip:
          "Media assets are still being processed. Suggested prompts will be generated once processing is complete and will include content from your videos and PDFs.",
      }
    }

    if (content.hasProcessedMediaAssets) {
      return {
        canGenerate: true,
        message: "Suggested Prompts",
        tooltip: content.hasUnprocessedMediaAssets
          ? "Prompts are generated from your description and processed media assets. Additional media assets are still processing and will enhance future generations."
          : "Prompts are generated from your description and attached media assets.",
      }
    }

    // Description is short and no assets available
    return {
      canGenerate: false,
      message: "Content is too short to generate suggested prompts",
      tooltip:
        "To generate suggested prompts, please add more content to your description (at least 500 characters) or attach videos/PDFs with content.",
    }
  }

  const generationStatus = getGenerationStatus()

  useEffect(() => {
    // Only auto-generate when:
    // User can manage content, no existing related questions, it's not currently generating and we have sufficient content
    const shouldAutoGenerate =
      canManage &&
      relatedQuestions.length === 0 &&
      !isMutating &&
      (descriptionIsLongEnough || content.hasProcessedMediaAssets)

    if (shouldAutoGenerate) {
      handleRegenerate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relatedQuestions.length, descriptionIsLongEnough, content.hasProcessedMediaAssets])

  // Determine if we should show media processing warning
  const showMediaProcessingWarning =
    canManage && content.hasUnprocessedMediaAssets && !canGeneratePrompts

  if (isMutating) {
    return (
      <div className={classes.wrapper}>
        <div className={classes.header}>
          <div className={classes.titleWrapper}>
            <div className={classes.leftTitleGroup}>
              <DiscoIcon icon={"stars"} width={20} height={20} active />
              <DiscoText variant={"heading-xs-700"}>
                {"Generating Suggested Prompts"}
              </DiscoText>
              <DiscoLoaderDots size={"mini"} dotClassName={classes.loaderDot} />
            </div>
            {content.hasUnprocessedMediaAssets && (
              <DiscoChip label={"Media Processing..."} color={"yellow"} />
            )}
          </div>
        </div>
      </div>
    )
  }

  if (!canManage && relatedQuestions.length === 0) {
    return null
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <div className={classes.titleWrapper}>
          <div className={classes.leftTitleGroup}>
            <DiscoIcon icon={"stars"} width={20} height={20} active />
            <DiscoText variant={"heading-xs-700"}>{generationStatus.message}</DiscoText>
            {canManage && generationStatus.tooltip && (
              <DiscoTooltip content={generationStatus.tooltip} />
            )}
          </div>
          <div className={classes.chipContainer}>
            {canManage && content.hasUnprocessedMediaAssets && canGeneratePrompts && (
              <DiscoChip label={"Media Processing..."} color={"yellow"} />
            )}
            {canManage && !canGeneratePrompts && (
              <DiscoChip label={"Visible to Admin Only"} color={"yellow"} />
            )}
          </div>
        </div>
        <div className={classes.actionsWrapper}>
          {canManage && canGeneratePrompts && (
            <DiscoTooltip content={"Regenerate suggested prompts"}>
              <DiscoIconButton
                onClick={handleRegenerate}
                disabled={isMutating}
                testid={"regenerate-suggested-prompts-button"}
              >
                <DiscoIcon icon={"refresh"} width={20} height={20} />
              </DiscoIconButton>
            </DiscoTooltip>
          )}

          {relatedQuestions.length > 0 && (
            <DiscoIconButton
              onClick={() => setIsExpanded(!isExpanded)}
              testid={"expand-suggested-prompts-button"}
            >
              <DiscoIcon
                icon={"iconsax.arrow-down"}
                width={24}
                height={24}
                rotate={isExpanded ? undefined : "-90"}
              />
            </DiscoIconButton>
          )}
        </div>
      </div>
      {showMediaProcessingWarning && (
        <DiscoText variant={"body-sm"} color={"text.secondary"} padding={2}>
          {
            "Media assets such as videos or PDF attachments are still being processed and aren't yet available to Ask AI. This usually takes a few minutes. Anything else in the lesson description can be used by Ask AI right away."
          }
        </DiscoText>
      )}
      {relatedQuestions.length > 0 && (
        <Collapse
          in={isExpanded}
          timeout={"auto"}
          unmountOnExit
          style={{ width: "100%" }}
        >
          <DiscoDivider marginBottom={0} marginTop={0} />
          <div className={classes.questionsBoxContent}>
            {relatedQuestions.map((question) => (
              <ContentSuggestedPromptListItem
                key={question.id}
                contentUsageId={contentUsageId}
                questionKey={question}
              />
            ))}
          </div>
        </Collapse>
      )}
    </div>
  )
}

function ContentSuggestedPromptsListSkeleton() {
  const classes = useStyles()

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <div className={classes.titleWrapper}>
          <div className={classes.leftTitleGroup}>
            <DiscoIcon icon={"stars"} width={20} height={20} active />
            <DiscoText variant={"heading-xs-700"}>{"Suggested Prompts"}</DiscoText>
          </div>
        </div>
        <div className={classes.actionsWrapper}>
          <DiscoIconButton disabled>
            <DiscoIcon icon={"refresh"} width={20} height={20} />
          </DiscoIconButton>
          <DiscoIconButton disabled>
            <DiscoIcon icon={"iconsax.arrow-down"} width={24} height={24} />
          </DiscoIconButton>
        </div>
      </div>
      <Collapse in={true} timeout={"auto"} unmountOnExit style={{ width: "100%" }}>
        <DiscoDivider marginBottom={0} marginTop={0} />
        <div className={classes.questionsBoxContent}>
          <div className={classes.questionItem}>
            <Skeleton width={"100%"} />
          </div>
          <div className={classes.questionItem}>
            <Skeleton width={"100%"} />
          </div>
          <div className={classes.questionItem}>
            <Skeleton width={"100%"} />
          </div>
        </div>
      </Collapse>
    </div>
  )
}

const useStyles = makeUseStyles((theme) => ({
  wrapper: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    alignSelf: "stretch",
    width: "100%",
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: theme.measure.borderRadius.large,
    background: theme.palette.background.paper,
    boxShadow: theme.palette.groovyDepths.xs,
    marginTop: theme.spacing(0.5),
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    padding: theme.spacing(1.5, 2),
    gap: theme.spacing(2),
    "&:hover": {
      background: "transparent",
    },
  },
  titleWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexGrow: 1,
    padding: theme.spacing(1.5),
  },
  chipContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  leftTitleGroup: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  loaderDot: {
    backgroundColor: `${theme.palette.grey[700]} !important`,
  },
  actionsWrapper: {
    display: "flex",
    alignItems: "center",
  },
  questionsBoxContent: {
    width: "100%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(1.5),
  },
  questionItem: {
    width: "100%",
    justifyContent: "flex-start",
    textAlign: "left",
    padding: theme.spacing(1.5),
    borderRadius: theme.measure.borderRadius.medium,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "& .MuiButton-label, & .MuiButton-root, & > span": {
      textAlign: "left",
      width: "100%",
      display: "block",
    },
  },
}))

export default Relay.withSkeleton({
  component: memo(ContentSuggestedPromptsList),
  skeleton: ContentSuggestedPromptsListSkeleton,
})
